import React, { useState } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Plus, Trash2, Edit, Search } from 'lucide-react';
import { toast } from 'sonner';

export const PartsTab = ({ parts, loadData }) => {
  const [showPartDialog, setShowPartDialog] = useState(false);
  const [selectedPart, setSelectedPart] = useState(null);
  const [formData, setFormData] = useState({ name_code: '', client_name: '', description: '' });
  const [partOperations, setPartOperations] = useState([]);

  const [searchPart, setSearchPart] = useState('');

  const filteredParts = parts.filter(part => {
    const q = searchPart.toLowerCase();
    return !q || part.name?.toLowerCase().includes(q) || part.code?.toLowerCase().includes(q);
  });

  const openPartDialog = (part = null) => {
    setSelectedPart(part);
    if (part) {
      setFormData({
        name_code: part.code ? `${part.name} - ${part.code}` : part.name,
        client_name: part.client_name || '',
        description: part.description || ''
      });
      setPartOperations((part.operations || []).map(op => {
        const totalMin = parseFloat(op.duration_minutes) || 0;
        const mins = Math.floor(totalMin);
        const secs = Math.round((totalMin - mins) * 60);
        return { ...op, duration_minutes: mins, duration_seconds: secs };
      }));
    } else {
      setFormData({ name_code: '', client_name: '', description: '' });
      setPartOperations([]);
    }
    setShowPartDialog(true);
  };

  const addOperation = () => {
    setPartOperations([...partOperations, {
      id: Date.now().toString(),
      operation_number: partOperations.length + 1,
      operation_name: '',
      duration_minutes: 0,
      duration_seconds: 0
    }]);
  };

  const updateOperation = (id, field, value) => {
    setPartOperations(partOperations.map(op => op.id === id ? { ...op, [field]: value } : op));
  };

  const removeOperation = (id) => {
    setPartOperations(partOperations.filter(op => op.id !== id));
  };

  const handlePartSubmit = async (e) => {
    e.preventDefault();
    const nameCode = formData.name_code || '';
    let name = nameCode, code = '';
    if (nameCode.includes(' - ')) {
      const parts2 = nameCode.split(' - ');
      name = parts2[0].trim(); code = parts2[1].trim();
    } else if (nameCode.includes('-')) {
      const parts2 = nameCode.split('-');
      name = parts2[0].trim(); code = parts2[1].trim();
    }
    if (!name || !code) { toast.error('Моля въведете формат: "Име - Код"'); return; }

    for (const op of partOperations) {
      if (!op.operation_name || (parseFloat(op.duration_minutes || 0) + parseFloat(op.duration_seconds || 0) / 60) <= 0) {
        toast.error('Всички операции трябва да имат Име и времетраене'); return;
      }
    }

    try {
      const payload = {
        name, code,
        client_name: formData.client_name || null,
        description: formData.description || null,
        operations: partOperations.map(op => ({
          operation_number: op.operation_number,
          operation_name: op.operation_name,
          duration_minutes: parseFloat(op.duration_minutes || 0) + parseFloat(op.duration_seconds || 0) / 60
        }))
      };
      if (selectedPart) {
        await axios.put(`${API}/parts/${selectedPart.id}`, payload);
        toast.success('Детайлът е актуализиран');
      } else {
        await axios.post(`${API}/parts`, payload);
        toast.success('Детайлът е създаден');
      }
      setShowPartDialog(false);
      loadData();
    } catch {
      toast.error('Грешка при запазване на детайл');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Сигурни ли сте?')) return;
    try {
      await axios.delete(`${API}/parts/${id}`);
      toast.success('Детайлът е изтрит');
      loadData();
    } catch {
      toast.error('Грешка при изтриване');
    }
  };

  return (
    <>
      <Card className="p-4 sm:p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="heading-font text-xl font-bold uppercase">Детайли</h2>
          <Button data-testid="add-part-button" onClick={() => openPartDialog()} className="bg-red-600 hover:bg-red-700 font-bold uppercase text-sm">
            <Plus className="w-4 h-4 mr-1 sm:mr-2" /><span className="hidden sm:inline">Добави детайл</span><span className="sm:hidden">+ Нов</span>
          </Button>
        </div>
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            data-testid="part-search"
            placeholder="Търси по име или код (напр. ос, 0251)..."
            value={searchPart}
            onChange={(e) => setSearchPart(e.target.value)}
            className="pl-9 border-2"
          />
        </div>
        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <div className="inline-block min-w-full align-middle px-4 sm:px-0">
            <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="uppercase text-xs">Име</TableHead>
              <TableHead className="uppercase text-xs">Код</TableHead>
              <TableHead className="uppercase text-xs">Клиент</TableHead>
              <TableHead className="uppercase text-xs">Операции</TableHead>
              <TableHead className="uppercase text-xs">Действия</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {parts.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-600">Няма добавени детайли</TableCell></TableRow>
            ) : filteredParts.map((part) => (
              <TableRow key={part.id}>
                <TableCell>{part.name}</TableCell>
                <TableCell className="mono-font">{part.code}</TableCell>
                <TableCell className="text-slate-600">{part.client_name || '-'}</TableCell>
                <TableCell>{part.operations?.length || 0} операции</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button data-testid={`edit-part-${part.id}`} onClick={() => openPartDialog(part)} variant="ghost" size="sm" className="text-blue-600 hover:text-blue-700 hover:bg-blue-50">
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button data-testid={`delete-part-${part.id}`} onClick={() => handleDelete(part.id)} variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
            </Table>
          </div>
        </div>
      </Card>

      {/* Part Dialog */}
      <Dialog open={showPartDialog} onOpenChange={setShowPartDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="heading-font text-2xl uppercase">{selectedPart ? 'Редактиране на детайл' : 'Нов детайл'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handlePartSubmit} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm uppercase tracking-wide mb-1 block">Име и Код *</Label>
                <Input data-testid="part-name-code-input" required value={formData.name_code || ''} onChange={(e) => setFormData({ ...formData, name_code: e.target.value })} className="border-2" placeholder="Напр: Детайл A - DA-001" />
              </div>
              <div>
                <Label className="text-sm uppercase tracking-wide mb-1 block">Клиент</Label>
                <Input data-testid="part-client-input" value={formData.client_name || ''} onChange={(e) => setFormData({ ...formData, client_name: e.target.value })} className="border-2" placeholder="Клиент (опционално)" />
              </div>
            </div>
            <div>
              <Label className="text-sm uppercase tracking-wide mb-1 block">Описание</Label>
              <Textarea data-testid="part-description-input" value={formData.description || ''} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="border-2" rows={2} />
            </div>
            <div>
              <div className="flex justify-between items-center mb-3">
                <Label className="text-sm uppercase tracking-wide font-bold">Операции</Label>
                <Button type="button" onClick={addOperation} size="sm" className="bg-green-600 hover:bg-green-700">
                  <Plus className="w-4 h-4 mr-1" />Добави операция
                </Button>
              </div>
              {partOperations.length === 0 ? (
                <div className="text-center py-6 bg-slate-50 border-2 border-dashed rounded-sm">
                  <p className="text-slate-600">Няма добавени операции</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {partOperations.map((op) => (
                    <div key={op.id} className="border-2 border-slate-200 rounded-sm p-3">
                      <div className="flex gap-2 items-start mb-2">
                        <div className="flex-shrink-0 w-20">
                          <Label className="text-xs uppercase mb-1 block">Номер</Label>
                          <Input type="number" value={op.operation_number} onChange={(e) => updateOperation(op.id, 'operation_number', parseInt(e.target.value))} onFocus={(e) => e.target.select()} className="h-9 border-2 text-center font-bold text-sm" required />
                        </div>
                        <div className="flex-1">
                          <Label className="text-xs uppercase mb-1 block">Операция *</Label>
                          <Input value={op.operation_name} onChange={(e) => updateOperation(op.id, 'operation_name', e.target.value)} className="h-9 border-2 text-sm" placeholder="Име на операция" required />
                        </div>
                        <Button type="button" onClick={() => removeOperation(op.id)} variant="ghost" size="sm" className="mt-5 text-red-600 hover:text-red-700 hover:bg-red-50 shrink-0">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <div>
                        <Label className="text-xs uppercase mb-1 block">Времетраене *</Label>
                        <div className="flex gap-2 items-center">
                          <Input
                            type="number" min="0"
                            value={op.duration_minutes === 0 ? '' : op.duration_minutes}
                            onChange={(e) => updateOperation(op.id, 'duration_minutes', Math.max(0, parseInt(e.target.value) || 0))}
                            onFocus={(e) => e.target.select()}
                            className="h-9 border-2 text-center w-16 text-sm"
                            placeholder="0"
                          />
                          <span className="text-xs text-slate-500 shrink-0">мин</span>
                          <Input
                            type="number" min="0" max="59"
                            value={op.duration_seconds === 0 ? '' : op.duration_seconds}
                            onChange={(e) => updateOperation(op.id, 'duration_seconds', Math.min(59, Math.max(0, parseInt(e.target.value) || 0)))}
                            onFocus={(e) => e.target.select()}
                            className="h-9 border-2 text-center w-16 text-sm"
                            placeholder="0"
                          />
                          <span className="text-xs text-slate-500 shrink-0">сек</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex gap-3">
              <Button type="submit" className="flex-1 bg-red-600 hover:bg-red-700 h-12 font-bold uppercase">Запази</Button>
              <Button type="button" onClick={() => setShowPartDialog(false)} variant="outline" className="flex-1 h-12 border-2 font-semibold uppercase">Отказ</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};
